import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} Bounds
 * @property {number} startY
 * @property {number} endY
 */

/**
 * `<scroll-scrub-video>`
 *
 * Scrubs a video's currentTime based on window scroll without creating extra scroll
 * distance or pinning the page.
 *
 * Attributes:
 * - src: required video url
 * - firefox-src: optional video url used in Firefox
 * - min-width: optional min viewport width (px) to enable scrubbing
 * - start-time: optional start time in seconds (default 0)
 * - end-time: optional end time in seconds (0 = full duration)
 * - preload: "metadata" (default) or "auto"
 * - accessibility-label: optional aria-label for the underlying <video>
 * - start-offset-px: scrub starts when the page is this many px ABOVE the section (default 0)
 */
class ScrollScrubVideo extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {string} */
  #currentSrc = '';

  /** @type {IntersectionObserver | null} */
  #io = null;

  /** @type {ResizeObserver | null} */
  #ro = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number | null} */
  #loopRafId = null;

  /** @type {number} */
  #lastScrollAt = 0;

  /** @type {number} */
  #lastSeekAt = 0;

  /** @type {boolean} */
  #active = false;

  /** @type {boolean} */
  #enabled = false;

  /** @type {boolean} */
  #hasMetadata = false;

  /** @type {Bounds} */
  #bounds = { startY: 0, endY: 0 };

  /** @type {number} */
  #startTime = 0;

  /** @type {number} */
  #endTime = 0;

  /** @type {number} */
  #duration = 0;

  connectedCallback() {
    if (this.shadowRoot) return;

    this.#enabled = this.#shouldEnable();
    this.#render();

    this.#io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.#setActive(true);
        } else {
          this.#setActive(false);
        }
      },
      // Start tracking a bit before entering the viewport so bounds/time are warmed up.
      { root: null, threshold: 0, rootMargin: '200px 0px 200px 0px' }
    );
    this.#io.observe(this);

    this.#ro = new ResizeObserver(() => {
      this.#updateBounds();
      this.#scheduleUpdate();
    });
    this.#ro.observe(this);

    window.addEventListener('resize', this.#handleWindowResize, { passive: true });
  }

  disconnectedCallback() {
    this.#setActive(false);

    this.#io?.disconnect();
    this.#io = null;

    this.#ro?.disconnect();
    this.#ro = null;

    window.removeEventListener('resize', this.#handleWindowResize);
  }

  static get observedAttributes() {
    return [
      'src',
      'firefox-src',
      'min-width',
      'start-time',
      'end-time',
      'preload',
      'accessibility-label',
      'start-offset-px',
    ];
  }

  attributeChangedCallback() {
    // If attributes change (theme editor), re-evaluate enablement and rebuild the video element.
    if (!this.shadowRoot) return;
    const nextEnabled = this.#shouldEnable();
    const enabledChanged = nextEnabled !== this.#enabled;
    this.#enabled = nextEnabled;
    if (enabledChanged) {
      this.#render();
    } else {
      this.#syncVideoAttributes();
      this.#updateBounds();
      this.#scheduleUpdate();
    }
  }

  #shouldEnable() {
    if (prefersReducedMotion()) return false;
    const minWidth = this.#readNumberAttr('min-width', 0);
    if (minWidth > 0 && window.innerWidth < minWidth) return false;
    return true;
  }

  #render() {
    const shadow = this.shadowRoot ?? this.attachShadow({ mode: 'open' });

    shadow.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
        }

        video {
          width: 100%;
          height: auto;
          display: block;
          border-radius: inherit;
          background: transparent;
        }

        :host([data-disabled]) {}
      </style>

      <video part="video"></video>
    `;

    this.#video = shadow.querySelector('video');
    if (!this.#video) return;

    this.#hasMetadata = false;
    this.#duration = 0;
    this.#startTime = 0;
    this.#endTime = 0;

    if (!this.#enabled) {
      this.setAttribute('data-disabled', '');
    } else {
      this.removeAttribute('data-disabled');
    }

    this.#syncVideoAttributes();

    this.#video.addEventListener('loadedmetadata', this.#handleLoadedMetadata, { once: true });
    this.#video.addEventListener('error', this.#handleVideoError);

    // Initialize bounds and time asap.
    this.#updateBounds();
    this.#scheduleUpdate();
  }

  #syncVideoAttributes() {
    if (!this.#video) return;

    const preload = (this.getAttribute('preload') || 'metadata').toLowerCase();
    const label = this.getAttribute('accessibility-label') || '';

    this.#video.preload = preload === 'auto' ? 'auto' : 'metadata';
    this.#video.muted = true;
    this.#video.playsInline = true;
    this.#video.setAttribute('playsinline', '');
    this.#video.setAttribute('webkit-playsinline', '');

    // If scrubbing is disabled, let the user control playback.
    this.#video.controls = !this.#enabled;

    if (label) this.#video.setAttribute('aria-label', label);
    else this.#video.removeAttribute('aria-label');

    // Set src last, then explicitly load (helps iOS/Safari reliably fire metadata events).
    const src = this.#chooseSource();
    const nextSrc = src || '';
    const srcChanged = nextSrc && this.#currentSrc !== nextSrc;
    if (srcChanged) {
      this.#currentSrc = nextSrc;
      this.#video.src = nextSrc;
      try {
        this.#video.load();
      } catch {
        // ignore
      }
    }
  }

  #chooseSource() {
    const src = this.getAttribute('src') || '';
    const firefoxSrc = this.getAttribute('firefox-src') || '';
    const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
    return (isFirefox && firefoxSrc) || src;
  }

  #handleLoadedMetadata = () => {
    if (!this.#video) return;

    this.#hasMetadata = true;
    this.#duration = Number.isFinite(this.#video.duration) ? this.#video.duration : 0;

    this.#startTime = Math.max(0, this.#readNumberAttr('start-time', 0));

    const rawEnd = this.#readNumberAttr('end-time', 0);
    const inferredEnd = rawEnd > 0 ? rawEnd : this.#duration;
    this.#endTime = Math.max(this.#startTime, inferredEnd);

    // Ensure we're at a deterministic start position.
    try {
      this.#video.currentTime = this.#startTime;
    } catch {
      // Some browsers may throw if seeking before enough data is loaded; we'll retry on scroll.
    }

    this.#scheduleUpdate();
  };

  #handleVideoError = () => {
    // No-op: fails silently. (Could add debug logging in the future.)
  };

  #handleScroll = () => {
    this.#lastScrollAt = performance.now();
    this.#startScrollLoop();
  };

  #handleWindowResize = () => {
    this.#enabled = this.#shouldEnable();
    // Toggle disabled state without a full rebuild.
    if (!this.#enabled) this.setAttribute('data-disabled', '');
    else this.removeAttribute('data-disabled');
    if (this.#video) this.#video.controls = !this.#enabled;

    this.#updateBounds();
    this.#scheduleUpdate();
  };

  #setActive(next) {
    if (next === this.#active) return;
    this.#active = next;

    if (this.#active) {
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.#updateBounds();
      this.#lastScrollAt = performance.now();
      this.#startScrollLoop();
      this.#primeDecode();
    } else {
      window.removeEventListener('scroll', this.#handleScroll);
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
      this.#stopScrollLoop();
    }
  }

  #scheduleUpdate() {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  }

  #startScrollLoop() {
    if (this.#loopRafId !== null) return;

    const tick = () => {
      this.#loopRafId = null;
      this.#update();

      // Keep looping briefly after scroll ends to capture inertial scrolling smoothly.
      if (!this.#active) return;
      if (performance.now() - this.#lastScrollAt < 160) {
        this.#loopRafId = requestAnimationFrame(tick);
      }
    };

    this.#loopRafId = requestAnimationFrame(tick);
  }

  #stopScrollLoop() {
    if (this.#loopRafId === null) return;
    cancelAnimationFrame(this.#loopRafId);
    this.#loopRafId = null;
  }

  #primeDecode() {
    // Best-effort "warm up" to reduce the first seek hitch; ignore failures.
    if (!this.#enabled) return;
    if (!this.#video) return;
    if (!this.#hasMetadata) return;

    try {
      this.#video.pause();
      const p = this.#video.play();
      if (p && typeof p.then === 'function') {
        p.then(() => this.#video?.pause()).catch(() => {});
      } else {
        this.#video.pause();
      }
    } catch {
      // ignore
    }
  }

  #updateBounds() {
    const rect = this.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const topY = rect.top + scrollY;

    const startOffsetPx = Math.max(0, this.#readNumberAttr('start-offset-px', 0));

    this.#bounds = {
      startY: topY - startOffsetPx,
      // Scrub completes by the time you've naturally scrolled through the element's height.
      endY: topY + rect.height,
    };
  }

  #update() {
    if (!this.#enabled) return;
    if (!this.#video) return;
    if (!this.#hasMetadata || this.#duration <= 0) return;

    const { startY, endY } = this.#bounds;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    const denom = endY - startY;
    if (!Number.isFinite(denom) || denom <= 0) return;

    const progress = this.#clamp((scrollY - startY) / denom, 0, 1);
    const span = Math.max(0, this.#endTime - this.#startTime);
    const nextTime = this.#startTime + progress * span;

    // Avoid micro-seeking every frame; helps Safari / low-power devices.
    if (Math.abs(this.#video.currentTime - nextTime) < 0.04) return;

    // Rate limit seeks a bit (seeking can be expensive and cause jank).
    const now = performance.now();
    if (now - this.#lastSeekAt < 16) return;
    this.#lastSeekAt = now;

    try {
      // fastSeek can be more performant where supported.
      // @ts-ignore - not in all TS lib defs
      if (typeof this.#video.fastSeek === 'function') this.#video.fastSeek(nextTime);
      else this.#video.currentTime = nextTime;
    } catch {
      // Ignore seek errors (can happen during buffering).
    }
  }

  #readNumberAttr(name, fallback) {
    const raw = this.getAttribute(name);
    if (raw == null || raw === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  #readCssNumber(value, fallback) {
    const n = parseFloat(String(value).trim());
    return Number.isFinite(n) ? n : fallback;
  }

  #clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }
}

if (!customElements.get('scroll-scrub-video')) {
  customElements.define('scroll-scrub-video', ScrollScrubVideo);
}


